<template>
  <div>
    <Navbar title="職歴">
      <template v-slot:right>
        <div class="form-save">
          <button type="button" class="form-save-button" @click="submit" :disabled="!isValid">保存</button>
        </div>
      </template>
    </Navbar>

    <form class="l-content" @submit.prevent="submit">
      <div class="l-container">
        <div class="p-container">
          <div class="form-title">概要</div>

          <div class="u-ptxl">
            <SuggestField
              :value="occupation.company_name"
              @input="onInputCompanyName"
              label="会社名"
              :suggestion-list="workerOccupationCompanies"
              display-attribute="company_name"
              required
              is-publish>
            </SuggestField>
          </div>

          <div class="u-ptm">
            <Dropdown
              :options="industriesSelect"
              label="業種"
              :disabled="this.occupation.worker_occupation_company_id != null"
              required
              is-publish
              v-model="occupation.industry_id">
            </Dropdown>
          </div>

          <div class="u-ptm">
            <Dropdown :options="occupationsSelect" label="職種カテゴリ" required is-publish v-model="occupation.occupation_id"></Dropdown>
          </div>

          <div class="u-ptm">
            <Dropdown :options="weeklyDayWorkings" label="勤務回数" required is-publish v-model="occupation.weekly_day_working"></Dropdown>
          </div>

          <div class="u-ptm">
            <TextField label="業務内容など" type="textarea" v-model="occupation.remarks"></TextField>
          </div>

          <div class="u-ptm">
            <Dropdown
              :options="employmentStatuses"
              label="雇用形態"
              required
              is-publish
              v-model="occupation.employment_status">
            </Dropdown>
          </div>

          <div class="u-ptm form-is-working-switch">
            <div class="label">
              <label>現在働いていますか？</label>
              <span class="u-color-danger">*</span>
            </div>
            <div class="u-pts switch-group">
              <span class="u-prxs">いいえ</span>
              <SliderSwitch v-model="occupation.is_working" @change="onChangeIsWorking"></SliderSwitch>
              <span class="u-plxs">はい</span>
            </div>
          </div>

          <div class="u-ptm">
            <TextField label="入社月" type="month" required is-publish v-model="occupation.working_started_at" :error-message="errors.working_started_at"></TextField>
          </div>

          <div class="u-ptm" v-if="!occupation.is_working">
            <TextField label="退社月" type="month" required is-publish v-model="occupation.working_ended_at" :error-message="errors.working_ended_at"></TextField>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="babel">
import Navbar from "@/components/navbar/Navbar";
import TextField from "@/components/TextField";
import Dropdown from "@/components/Dropdown";
import { mapActions, mapGetters } from "vuex";
import WorkerOccupationCompanies from "@/api/v1/worker_occupation_companies";
import SuggestField from "@/components/SuggestField";
import EMPLOYMENT_STATUSES from "@/constants/employmentStatuses";
import WEEKLY_DAY_WORKINGS from "@/constants/weeklyDayWorkings";
import SliderSwitch from "@/components/SliderSwitch";
import {SIGNUP_STORE} from "@/constants/InjectionKeys";
import {inject} from "vue";

export default {
  name: "WorkHistory",
  components: {
    SliderSwitch,
    SuggestField,
    TextField,
    Navbar,
    Dropdown
  },
  data() {
    return {
      workerOccupationCompanies: [],
      occupation: {},
      errors: {},
      signupStore: null
    }
  },
  computed: {
    ...mapGetters('signup', ['industries', 'occupations']),
    employmentStatuses() {
      return EMPLOYMENT_STATUSES;
    },
    isValid() {
      this.errors = {};

      return ![
        this.isValidCompanyName(),
        this.isValidIndustryId(),
        this.isValidOccupationId(),
        this.isValidWeeklyDayWorking(),
        this.isValidEmploymentStatus(),
        this.isValidWorkingStartedAt(),
        this.isValidWorkingEndedAt()
      ].includes(false);
    },
    industriesSelect() {
      return this.industries.map((industry) => ({ name: industry.name, value: industry.id }));
    },
    occupationsSelect() {
      return this.occupations.map((occupation) => ({ name: occupation.name, value: occupation.id }));
    },
    weeklyDayWorkings() {
      return WEEKLY_DAY_WORKINGS;
    },
  },
  mounted() {
    this.fetchIndustries();
    this.fetchOccupations();
  },
  created() {
    this.$emit('page-navbar', false);
    this.signupStore = inject(SIGNUP_STORE)

    if (this.signupStore.worker.occupations) {
      this.occupation = this.signupStore.worker.occupations[0]
    } else {
      this.occupation = {}
    }
  },
  methods: {
    ...mapActions('signup', ['fetchIndustries', 'fetchOccupations']),
    onChangeIsWorking(event) {
      this.occupation.is_working = event.target.checked;
      if (this.occupation.is_working) {
        this.occupation.working_ended_at = null;
      }
    },
    submit() {
      this.signupStore.setWorker({
        ...this.signupStore.worker,
        occupations: [this.occupation]
      });

      this.$router.back();
    },
    isValidCompanyName() {
      return this.occupation.company_name != null && this.occupation.company_name !== '';
    },
    isValidIndustryId() {
      return this.occupation.industry_id != null;
    },
    isValidOccupationId() {
      return this.occupation.occupation_id != null;
    },
    isValidWeeklyDayWorking() {
      return this.occupation.weekly_day_working != null;
    },
    isValidEmploymentStatus() {
      return this.occupation.employment_status != null;
    },
    isValidWorkingStartedAt() {
      if (this.occupation.working_started_at != null) {
        const now = new Date();
        const workingStartedAt = new Date(this.occupation.working_started_at);

        if (now <= workingStartedAt) {
          this.errors.working_started_at = '入社月は現在より前に設定してください';
        } else {
          return true;
        }
      }

      return false;
    },
    isValidWorkingEndedAt() {
      // 働いてる場合は退社月の検証しない
      if (this.occupation.is_working) {
        return true;
      }

      if (this.occupation.working_ended_at != null && this.occupation.working_started_at != null) {
        const startedAt = new Date(this.occupation.working_started_at);
        const endedAt = new Date(this.occupation.working_ended_at);

        // 退社月が未来なこと
        if (endedAt >= startedAt) {
          return true;
        }

        this.errors.working_ended_at = '退社月は入社月より先を設定してください';
      }

      return false;
    },
    onInputCompanyName(name) {
      this.occupation.company_name = name

      // FIXME: すごい微妙な実装な気がする...
      if (name) {
        this.getCompanySuggestions(name).then(() => {
          const company = this.workerOccupationCompanies.find((company) => company.company_name === name);

          if (company) {
            this.$set(this.occupation, 'worker_occupation_company_id', company.id)
            this.$set(this.occupation, 'industry_id', company.industry_id)
          } else {
            this.$set(this.occupation, 'worker_occupation_company_id', null)
          }
        })
      } else {
        this.$set(this.occupation, 'worker_occupation_company_id', undefined)
        this.$set(this.occupation, 'industry_id', undefined)
      }
    },
    getCompanySuggestions(company_name) {
      return new Promise((resolve) => {
        WorkerOccupationCompanies.getAll({ q: company_name }, (response) => {
          this.workerOccupationCompanies = response.data.payload;
          resolve()
        });
      })
    }
  }
}
</script>

<style scoped lang="scss">
.form {
  &-title {
    font-weight: bold;
    font-size: 18px;
    line-height: 18px;
  }

  &-save-button {
    background: #F94848;
    border: none;
    border-radius: 60px;
    color: white;
    padding: 8px 14px;

    font-weight: 500;
    font-size: 14px;
    line-height: 14px;

    &:disabled {
      background: #E0E0E0;
    }
  }

  &-delete-button {
    width: 100%;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 3px;

    span {
      color: white;

      font-weight: 500;
      font-size: 14px;
      line-height: 14px;
    }
  }

  &-is-working-switch {
    label {
      color: #CAC9C9;
    }

    .switch-group {
      display: flex;
      align-items: center;

      color: #CAC9C9;
    }
  }
}
</style>
